// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useSnackbar } from 'notistack';
import React, { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Box, Tab, Tabs, Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { BridgeAgreement } from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/Bridge';
import {
  BridgeUserAgreement,
  BridgeUserAgreementRequest,
} from '@daml.js/utility-bridge-v0/lib/Utility/Bridge/V0/Agreement/User/module';
import { ContractId } from '@daml/types';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import { RejectDialog } from '../../../components/Dialog/RejectDialog';
import Loading from '../../../components/Loading';
import { NotificationBadge } from '../../../components/NotificationBadge';
import PartyDisplay from '../../../components/PartyDisplay';
import { DataTable } from '../../../components/Table/DataTable';
import { useParties } from '../../../contexts/PartiesContext';
import useTerminateBridgeUserAgreement from '../../../hooks/mutations/bridge/onboarding/useTerminateBridgeUserAgreement';
import useMutate from '../../../hooks/mutations/useMutate';
import useBridgeUserAgreementRequests from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreementRequests';
import useBridgeUserAgreements from '../../../hooks/queries/bridge/agreement/useBridgeUserAgreements';
import useBridgeInstrumentConfiguration from '../../../hooks/queries/bridge/configuration/useBridgeInstrumentConfiguration';
import {
  ERR_ACC_BRIDGE_USER_AGREEMENT,
  ERR_REJ_BRIDGE_USER_AGREEMENT,
  ERR_TER_BRIDGE_USER_AGREEMENT,
  SUC_ACC_BRIDGE_USER_AGREEMENT,
  SUC_REJ_BRIDGE_USER_AGREEMENT,
  SUC_TER_BRIDGE_USER_AGREEMENT,
} from '../../../utils/strings';
import { RequestBridgeUserAgreementDialog } from './RequestBridgeUserAgreement';
import useAcceptBridgeUserAgreement from '../../../hooks/mutations/bridge/onboarding/useAcceptBridgeUserAgreement';
import useRejectBridgeUserAgreement from '../../../hooks/mutations/bridge/onboarding/useRejectBridgeUserAgreement';
import useBridgeAgreement from '../../../hooks/queries/bridge/agreement/useBridgeAgreement';
import useBridgeService from '../../../hooks/queries/bridge/agreement/useBridgeService';
import useBridgeAgreementOffer from '../../../hooks/queries/bridge/agreement/useBridgeAgreementOffer';
import useBlacklistedUsers from '../../../hooks/queries/admin/useBlacklistedUsers';
import { AcceptBridgeDialog } from './AcceptBridge';

type Row = {
  contractId: string;
  instrument: string;
  bridgeOperator: string;
  attestor: string;
  user: string;
};

type BridgeRow = {
  contractId: string;
  instrument: string;
  operator: string;
  bridgeOperator: string;
  attestor: string;
  type: string
};

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

type BridgeAction = {
  name: string;
  action: (r: BridgeRow) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'Instrument',
      headerName: 'Instrument',
      renderCell: (params) => <PartyDisplay party={params.row.instrument} />,
      flex: 75,
      filterable: false,
    },
    {
      field: 'Admin',
      headerName: 'Admin',
      renderCell: (params) => <PartyDisplay party={params.row.attestor} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'user',
      headerName: 'User',
      renderCell: (params) => <PartyDisplay party={params.row.user} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 75,
      filterable: false,
    },
  ];
};

const createBridgeHeaders: (createActions: (r: BridgeRow) => BridgeAction[]) => GridColDef<BridgeRow>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<BridgeRow>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'Instrument',
      headerName: 'Instrument',
      renderCell: (params) => <PartyDisplay party={params.row.instrument} />,
      flex: 100,
      filterable: false,
    },
    {
      field: 'bridgeOperator',
      headerName: 'Bridge Operator',
      renderCell: (params) => <PartyDisplay party={params.row.bridgeOperator} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'admin',
      headerName: 'Admin',
      renderCell: (params) => <PartyDisplay party={params.row.attestor} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'operator',
      headerName: 'Utility Operator',
      renderCell: (params) => <PartyDisplay party={params.row.operator} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'type',
      headerName: 'Type',
      renderCell: (params) => <PartyDisplay party={params.row.type} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [bridgeUserIsOpen, setBridgeUserIsOpen] = useState<boolean>(false);
  const bridgeUserAgreements = useBridgeUserAgreements();
  const bridgeUserAgreementRequests = useBridgeUserAgreementRequests();
  const { bridgeOperator, party } = useParties();

  const isLoading = bridgeUserAgreements.isLoading || bridgeUserAgreementRequests.isLoading;
  if (isLoading) return null;

  const hasService = !!bridgeUserAgreements.data?.find(
    (r) => r.payload.user === party && r.payload.bridgeOperator === bridgeOperator,
  );
  const hasPendingRequest = !!bridgeUserAgreementRequests.data?.find(
    (r) => r.payload.user === party && r.payload.bridgeOperator === bridgeOperator,
  );

  return (
    <GridToolbarContainer>
      {!!bridgeUserIsOpen && (
        <RequestBridgeUserAgreementDialog
          open={!!bridgeUserIsOpen}
          onClose={() => setBridgeUserIsOpen(false)}
        />
      )}
      <SyncButton
        onClick={() => setBridgeUserIsOpen(true)}
        disabled={hasService || hasPendingRequest}
      >
        Request User Agreement
      </SyncButton>
    </GridToolbarContainer>
  );
};

export const Onboarding = () => {

  const [value, setValue] = React.useState('0');
  const onChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };
  const [onInitialize, setOnInitialize] = useState(false);
  const [onReject, setOnReject] = useState<((reason: string) => Promise<unknown>) | undefined>();
  const { party, bridgeOperator } = useParties();
  const mutate = useMutate();
  const terminateBridgeUserAgreementService = useTerminateBridgeUserAgreement();
  const rejectBridgeUserAgreement = useRejectBridgeUserAgreement();
  const bridgeAgreement = useBridgeAgreement();
  const bridgeService = useBridgeService();
  const bridgeAgreementOffer = useBridgeAgreementOffer();
  const bridgeInstrumentConfig = useBridgeInstrumentConfiguration();
  const acceptBridgeUserAgreement = useAcceptBridgeUserAgreement();
  const blacklistedUsers = useBlacklistedUsers();
  const bridgeUserAgreements = useBridgeUserAgreements();
  const bridgeUserAgreementRequests = useBridgeUserAgreementRequests();
  const { enqueueSnackbar } = useSnackbar();
  const { showBoundary } = useErrorBoundary<Error>();

  const terminateBridgeUserAgreement = useCallback(
    (r: Row) => {
      const payload = {
        bridgeUserAgreementCid: r.contractId as ContractId<BridgeUserAgreement>,
        reason: 'revoked',
        actor: party,
      };
      return mutate(
        terminateBridgeUserAgreementService,
        payload,
        SUC_TER_BRIDGE_USER_AGREEMENT,
        ERR_TER_BRIDGE_USER_AGREEMENT,
      );
    },
    [terminateBridgeUserAgreementService, mutate],
  );

  const acceptBridgeUserAgreementRequest = useCallback(
    (row: Row) => {
      const isBlacklisted = blacklistedUsers.data?.some(
        (blacklistedUser) => blacklistedUser.partyId === row.user && !blacklistedUser.deletedAt,
      );
      if (isBlacklisted) {
        enqueueSnackbar('Cannot accept: user is blacklisted', { variant: 'error' });
        return Promise.resolve();
      }

      // Check if data is loaded
      if (!bridgeInstrumentConfig.data) {
        showBoundary(
          new Error(ERR_ACC_BRIDGE_USER_AGREEMENT, {
            cause: `Bridge Instrument configuration is not available.`,
          }),
        );
        return Promise.resolve();
      }
      if (!bridgeAgreement.data) {
        showBoundary(
          new Error(ERR_ACC_BRIDGE_USER_AGREEMENT, { cause: 'Bridge agreement is not available.' }),
        );
        return Promise.resolve();
      }

      // Accept the request
      const payload = {
        bridgeAgreementCid: bridgeAgreement.data.contractId,
        bridgeUserAgreementRequestCid: row.contractId as ContractId<BridgeUserAgreementRequest>,
        instrumentConfigurationCid: bridgeInstrumentConfig.data.contractId,
      };
      return mutate(
        acceptBridgeUserAgreement,
        payload,
        SUC_ACC_BRIDGE_USER_AGREEMENT,
        ERR_ACC_BRIDGE_USER_AGREEMENT,
      );
    },
    [
      bridgeAgreement,
      acceptBridgeUserAgreement,
      bridgeInstrumentConfig,
      blacklistedUsers,
      mutate,
      showBoundary,
      enqueueSnackbar,
    ],
  );

  const rejectBridgeUserAgreementRequest = useCallback(
    (r: Row, bridgeAgreementCid: ContractId<BridgeAgreement>, reason: string) => {
      const payload = {
        bridgeAgreementCid,
        bridgeUserAgreementRequestCid: r.contractId as ContractId<BridgeUserAgreementRequest>,
        reason,
      };
      return mutate(
        rejectBridgeUserAgreement,
        payload,
        SUC_REJ_BRIDGE_USER_AGREEMENT,
        ERR_REJ_BRIDGE_USER_AGREEMENT,
      );
    },
    [rejectBridgeUserAgreement, mutate],
  );

  const createBridgeActions = useCallback(
    (r: BridgeRow) => {
      const actions: BridgeAction[] = [];
      if (r.bridgeOperator === party && r.type == "Bridge Agreement Offer")
        actions.push({ name: 'Initialize', action: () => Promise.resolve(setOnInitialize(true)) });
      return actions;
    },
    [party, terminateBridgeUserAgreement],
  );

  const createServiceActions = useCallback(
    (r: Row) => {
      const actions: Action[] = [];
      if (r.bridgeOperator === party)
        actions.push({ name: 'Terminate', action: terminateBridgeUserAgreement });
      return actions;
    },
    [party, terminateBridgeUserAgreement],
  );

  const createRequestActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.bridgeOperator === party) {
      actions.push(
        { name: 'Accept', action: acceptBridgeUserAgreementRequest },
        {
          name: 'Reject',
          action: () => {
            setOnReject(
              () => (reason: string) =>
                rejectBridgeUserAgreementRequest(r, bridgeAgreement.data!.contractId, reason),
            );
            return Promise.resolve();
          },
        },
      );
    }
    return actions;
  };

  const isLoading =
    bridgeInstrumentConfig.isLoading ||
    bridgeUserAgreements.isLoading ||
    bridgeUserAgreementRequests.isLoading ||
    bridgeAgreement.isLoading ||
    bridgeService.isLoading ||
    bridgeAgreementOffer.isLoading ||
    !bridgeUserAgreementRequests.data ||
    !bridgeUserAgreements.data;
  if (isLoading) return <Loading />;

  const serviceRows = bridgeUserAgreements.data.map((r) => ({
    contractId: r.contractId as string,
    instrument: r.payload.instrumentId.id,
    operator: r.payload.operator,
    bridgeOperator: r.payload.bridgeOperator,
    attestor: r.payload.crossChainRepresentative,
    user: r.payload.user,
  }));

  const requestRows = bridgeUserAgreementRequests.data.map((r) => ({
    contractId: r.contractId as string,
    instrument: r.payload.instrumentId.id,
    bridgeOperator: r.payload.bridgeOperator,
    attestor: r.payload.crossChainRepresentative,
    user: r.payload.user,
  }));

  const bridgeRows = [
  ...(bridgeAgreement.data ? [{
    contractId: bridgeAgreement.data.contractId as string,
    instrument: bridgeAgreement.data.payload.instrumentId.id,
    operator: bridgeAgreement.data.payload.operator,
    bridgeOperator: bridgeAgreement.data.payload.bridgeOperator,
    attestor: bridgeAgreement.data.payload.crossChainRepresentative,
    type: "Bridge Agreement",
  }] : []),
  ...(bridgeService.data ? [{
    contractId: bridgeService.data.contractId as string,
    instrument: bridgeService.data.payload.instrumentIdentifier.id,
    operator: bridgeService.data.payload.operator,
    bridgeOperator: bridgeService.data.payload.bridgeOperator,
    attestor: bridgeService.data.payload.crossChainRepresentative,
    type: "Bridge Service",
  }] : []),
  ...(bridgeAgreementOffer.data ? [{
    contractId: bridgeAgreementOffer.data.contractId as string,
    instrument: bridgeAgreementOffer.data.payload.instrumentId,
    operator: bridgeAgreementOffer.data.payload.operator,
    bridgeOperator: bridgeAgreementOffer.data.payload.bridgeOperator,
    attestor: bridgeAgreementOffer.data.payload.crossChainRepresentative,
    type: "Bridge Agreement Offer",
  }] : []),
];

  return (
    <>
    {!!onInitialize && (
            <AcceptBridgeDialog
              open={!!onInitialize}
              onClose={() => setOnInitialize(false)}
            />
          )}
    {party === bridgeOperator && (<DataTable
            title="Bridge"
            enableTableWrapper
            variant="h3"
            columns={createBridgeHeaders(createBridgeActions)}
            rows={bridgeRows}
            rowKey={(a) => a.contractId}
          />)}
      {!!onReject && (
        <RejectDialog
          open={!!onReject}
          onClose={() => setOnReject(undefined)}
          onReject={onReject}
        />
      )}

      <Tabs value={value} onChange={onChange}>
        <Tab value="0" label="services" />
        <Tab
          value="1"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'row' }}>
              <Typography sx={{ mr: 1 }}>Requests</Typography>
              <NotificationBadge value={requestRows.length || 0} />
            </Box>
          }
        />
      </Tabs>

      {value === '0' && (
        <DataTable
          enableTableWrapper
          title=""
          variant="h3"
          columns={createHeaders(createServiceActions)}
          rows={serviceRows}
          rowKey={(a) => a.contractId}
          toolbar={Toolbar}
        />
      )}
      {value === '1' && (
        <DataTable
          enableTableWrapper
          title=""
          variant="h3"
          columns={createHeaders(createRequestActions)}
          rows={requestRows}
          rowKey={(a) => a.contractId}
        />
      )}
    </>
  );
};
